import { UNSAFE_NavigationContext, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePreventNavigationModal } from './usePreventNavigationModal';

interface UsePreventNavigationProps {
  shouldPrevent: boolean;
  onPrevent?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  defaultPath?: string | number;
}

export function usePreventNavigation(props: UsePreventNavigationProps) {
  const { shouldPrevent, onPrevent, onConfirm, onCancel, defaultPath } = props;
  const navigator = React.useContext(UNSAFE_NavigationContext).navigator as any;
  const [tx, setTx] = useState<any>(null);
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [nextLocation, setNextLocation] = useState<string | number | null>(null);
  const navigate = useNavigate();

  const handleConfirm = useCallback(() => {
    if (tx) {
      const currentTx = tx;
      setTx(null);
      setTargetPath(null);
      setTimeout(() => {
        currentTx.retry();
      }, 0);
      return true;
    }
    return false;
  }, [tx]);

  const {
    openLeaveModal,
    closeLeaveModal,
    Modal: PreventNavigationModal,
  } = usePreventNavigationModal({
    navigationTx: tx,
    nextLocation,
    targetPath,
    confirmNavigation: handleConfirm,
    navigate,
    defaultPath: defaultPath as any,
    onConfirm,
    onCancel,
  });

  useEffect(() => {
    let unblock: (() => void) | undefined;

    if (shouldPrevent) {
      unblock = navigator?.block?.((transaction: any) => {
        setTx(transaction);
        setTargetPath(transaction.location.pathname);
        openLeaveModal();
        onPrevent?.();
        return false;
      });
    } else {
      setTx(null);
      setTargetPath(null);
    }

    return () => {
      if (unblock) {
        unblock();
      }
    };
  }, [navigator, shouldPrevent, onPrevent, openLeaveModal]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldPrevent) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldPrevent]);

  // High-level API: navigate safely or open modal
  const navigateSafely = useCallback(
    (to?: string | number | null) => {
      const dest = to ?? defaultPath ?? -1;
      if (shouldPrevent) {
        setNextLocation(dest as any);
        openLeaveModal();
      } else {
        navigate(dest as any);
      }
    },
    [shouldPrevent, defaultPath, navigate, openLeaveModal],
  );

  const backHandler = useMemo(() => {
    return () => navigateSafely(-1);
  }, [navigateSafely]);

  return {
    tx,
    targetPath,
    confirmNavigation: handleConfirm,
    openLeaveModal,
    closeLeaveModal,
    PreventNavigationModal,
    setNextLocation, // kept for backward compatibility
    navigateSafely,
    backHandler,
  };
}
